import React, { useState, useLayoutEffect, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  useModal,
  useTranslation,
  StatusMessage,
} from "Shared/util/custom-hooks";
import { useEIdAuthentication } from "Shared/util/eid-authentication/eid-authentication-hook";
import { EIdAuthenticationPurpose, EIdProvider } from "Shared/util/types";
// @ts-ignore: No TypeScript support
import makeComponentTrashable from "trashable-react";
import {
  finalizeLoginWithEId,
  loginWithCredentials,
  loginWithMigration,
  decryptUserData,
} from "Shared/util/web-api";
import { setSessionCookie } from "Shared/util/user-session";
import InputText from "Shared/components/input-text";
import Button from "Shared/components/button";
import Spinner from "Shared/components/spinner";
import { getLink } from "Shared/navigation/login/routes";
import { joinClassNames as c } from "Shared/util/styles";
import TextLink from "Shared/components/text-link";
import {
  ToggleRadioGroup,
  ToggleRadioButton,
} from "Shared/components/toggle-radio";
import InformationModal from "Shared/components/modals/information-modal";
import useAppContext from "Contexts/app-context";
import styles from "./login-form.css";

enum LoginMethod {
  EId = "EId",
  Credentials = "Credentials",
}

type LoginFormProps = {
  registerPromise: (p: Promise<any>) => Promise<any>;
  dispatchStatusMessage: (action: StatusMessage) => void;
};

const LoginForm = ({
  registerPromise,
  dispatchStatusMessage,
}: LoginFormProps) => {
  const [email, setEmail] = useState("");
  const [isLoadingBankId, setIsLoadingBankId] = useState(false);
  const [isLoadingCreds, setIsLoadingCreds] = useState(false);
  const [loginMethod, setLoginMethod] = useState(LoginMethod.EId);
  const [isFinalizingEIdLogin, setIsFinalizingEIdLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [validEmail, setValidEmail] = useState(true);
  const [validPassword, setValidPassword] = useState(true);
  const [preselectedOrganizationNumber, setPreselectedOrganizationNumber] =
    useState<string>();
  const history = useHistory();
  const { isModalOpen, openModal, closeModal } = useModal();
  const tdb = useTranslation((x) => x.dbTranslations);
  const t = useTranslation((x) => x.login);
  const tg = useTranslation((x) => x.global);
  const tm = useTranslation((x) => x.migration);
  const te = useTranslation((x) => x.eId);
  const useEId = loginMethod === LoginMethod.EId;
  const hasEnteredCustomerNbr = validEmail && !email.includes("@");
  const isUpdate =
    !useEId && validEmail && validPassword && hasEnteredCustomerNbr;

  const {
    appData: { eIdProvider, privacyPolicyUrl, maintenance },
  } = useAppContext();

  useLayoutEffect(() => {
    dispatchStatusMessage({ type: "NONE" });
  }, [useEId]);

  const handleEIdAuthenticationSucceeded = (token: string) => {
    setIsFinalizingEIdLogin(true);

    registerPromise(finalizeLoginWithEId(token, preselectedOrganizationNumber))
      .then((userToken) => {
        setIsLoadingBankId(false);
        setSessionCookie(userToken);
        history.push(getLink("select-org"));
      })
      .catch(() => {
        setIsFinalizingEIdLogin(false);
        dispatchStatusMessage({
          type: "FAILED",
          message: t.somethingWentWrongWhileLogginIn,
        });
      });
  };

  const handleEIdAuthenticationFailed = () => {
    setIsLoadingBankId(false);
    dispatchStatusMessage({
      type: "FAILED",
      message: tdb.eidVerificationAborted,
    });
  };

  const handleEIdAuthenticationStarted = () => {
    dispatchStatusMessage({ type: "NONE" });
  };

  const {
    renderEIdAuthentication,
    resetEIdAuthentication,
    isAuthenticatingEId,
  } = useEIdAuthentication(
    registerPromise,
    EIdAuthenticationPurpose.UserSignIn,
    handleEIdAuthenticationSucceeded,
    handleEIdAuthenticationFailed,
    handleEIdAuthenticationFailed,
    handleEIdAuthenticationStarted,
    maintenance?.loginDisabled
  );

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.has("data")) {
      const data = query.get("data");
      if (data !== null) {
        registerPromise(decryptUserData(data)).then((options) => {
          if (options?.org) {
            setPreselectedOrganizationNumber(options.org);
          }
          const uri = window.location.toString();
          const cleanUri = uri.substring(0, uri.indexOf("?"));
          window.history.replaceState({}, document.title, cleanUri);
        });
      }
    }
  }, []);

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();

    // Login with migration
    if (isUpdate) {
      setIsLoadingCreds(true);
      registerPromise(loginWithMigration(email, password))
        .then((result) => {
          setIsLoadingCreds(false);
          if (result.token) {
            history.push({
              pathname: getLink("update-account"),
              state: { ugId: result.ugId, token: result.token },
            });
          } else {
            const [respFirstName, respLastName, respEmail] = result.split(" ");
            dispatchStatusMessage({
              type: "SUCCEEDED",
              message: t.format(t.yourCompanyUsesPersonalAccountsContact, {
                respFirstName,
                respLastName,
                respEmail,
              }),
            });
          }
        })
        .catch(() => {
          dispatchStatusMessage({
            type: "FAILED",
            message: t.somethingWentWrongMakeSure,
          });
          setIsLoadingCreds(false);
        });
    }

    // Login with credentials
    if (!useEId && !isUpdate) {
      setIsLoadingCreds(true);
      registerPromise(
        loginWithCredentials(email, password, preselectedOrganizationNumber)
      )
        .then((result) => {
          setIsLoadingCreds(false);
          setSessionCookie(result);
          history.push(getLink("select-org"));
        })
        .catch(() => {
          dispatchStatusMessage({
            type: "FAILED",
            message: t.incorrectEmailOrPasswordHaveYouActivatedAccount,
          });
          setIsLoadingCreds(false);
        });
    }
  };

  const loginText = isUpdate ? t.proceed : tg.logIn;
  const isLoading = useEId ? isLoadingBankId : isLoadingCreds;
  const isSubmitEnabled = validEmail && validPassword;

  const handleChangeLoginMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetEIdAuthentication();
    setLoginMethod(LoginMethod[e.target.value as keyof typeof LoginMethod]);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.toggleContainer}>
          <ToggleRadioGroup
            name="login-method-toggle"
            value={loginMethod}
            handleChange={handleChangeLoginMethod}
          >
            <ToggleRadioButton
              value={LoginMethod.EId}
              text={te[eIdProvider].eIdProviderShortName}
            />
            <ToggleRadioButton
              value={LoginMethod.Credentials}
              text={t.userName}
            />
          </ToggleRadioGroup>
        </div>

        {!useEId && (
          <form onSubmit={handleSubmitForm}>
            <InputText
              // @ts-ignore: TODO: Fix error
              inputType="email-or-customer-nbr"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              setEnabledSubmit={(value: boolean) => setValidEmail(value)}
              isRequired
            />
            <InputText
              // @ts-ignore: TODO: Fix error
              inputType="current-password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value)
              }
              setEnabledSubmit={(value: boolean) => setValidPassword(value)}
              isRequired
            />
            <Button
              className={styles.submitButton}
              buttonType="action-primary"
              isLoading={isLoading}
              text={loginText}
              attributes={{
                type: "submit",
                disabled: !isSubmitEnabled || maintenance?.loginDisabled,
              }}
            />
            <TextLink
              text={t.forgotPassword}
              link={() => history.push(getLink("forgot-password"))}
              className={styles.forgotPasswordTextLink}
            />
          </form>
        )}

        {useEId && (
          <>
            {isFinalizingEIdLogin ? (
              <Spinner />
            ) : (
              renderEIdAuthentication(
                c(
                  styles.eIdContainer,
                  eIdProvider === EIdProvider.SeBankId &&
                    !isAuthenticatingEId &&
                    styles.seBankId
                )
              )
            )}
          </>
        )}
        {!isAuthenticatingEId && !isFinalizingEIdLogin && (
          <TextLink
            text={t.loginWithCustomerNumber}
            link={openModal}
            className={styles.missingAccountTextLink}
          />
        )}
        {privacyPolicyUrl && !isAuthenticatingEId && !isFinalizingEIdLogin && (
          <TextLink
            text={te.privacyPolicy}
            link={privacyPolicyUrl}
            className={styles.privacyPolicyLink}
          />
        )}
      </div>

      <InformationModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        headerText={tm.migrationInstructionsSubject}
        contentText={tm.migrationInstructionsMessage}
      />

      {maintenance?.loginMessage && (
        <div className={styles.maintenanceBanner}>
          {maintenance.loginMessage}
        </div>
      )}
    </>
  );
};

export default makeComponentTrashable(LoginForm);
